import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import DataTableView from './DataTableView';
import { buildContinuationSummaries } from '../utils/scentMetrics';
import { formatRuntimeDuration } from '../utils/runtimeEstimate';

const RUNTIME_TEXT_COLOR = '#AA1414';

// Function to calculate luminance and determine text color for contrast
const calculateTextColor = (backgroundColor) => {
  // Convert hex to RGB
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  // Calculate relative luminance
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  
  // Return white text for dark backgrounds, black text for light backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const TreemapView = ({
  graphData,
  currentView,
  selectedNodeType,
  selectedEdgeType,
  nodeTypeSummary,
  edgeTypeSummary,
  filteredNodes,
  onNodeTypeClick,
  onEdgeTypeClick,
  onBackClick,
  settings,
  isCalculating,
  calculationProgress,
  edgeIndex,
  scentControls,
  metricOptions = [],
  runtimeEstimates = {},
  fanoutEstimates = {},
  showRuntimeEstimates = true
}) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '', title: '' });

  const metricId = !scentControls || scentControls.aggregate === 'count'
    ? 'count'
    : scentControls.aggregate === 'runtime'
      ? 'runtime'
      : scentControls.aggregate === 'runtime_x1000'
        ? 'runtime_x1000'
        : scentControls.aggregate === 'fanout'
          ? 'fanout'
          : `${scentControls.aggregate}:${scentControls.attribute || ''}`;
  const metricLabel = metricOptions.find(option => option.id === metricId)?.label || 'Count';
  const groupByLabel = scentControls?.groupBy || (currentView === 'edgeTypes' ? 'Edge Type' : 'Node Type');
  const isRuntimeTooltipLine = (line) => (
    line.startsWith('Expected next:') ||
    line.startsWith('Expected compute time:') ||
    line.startsWith('Expected compute time x1000:')
  );
  const getRuntimeEstimate = useCallback((item) => (
    item.runtimeEstimate ||
    runtimeEstimates[item.id] ||
    runtimeEstimates[item.type] ||
    runtimeEstimates[item.label] ||
    null
  ), [runtimeEstimates]);
  const getFanoutEstimate = useCallback((item) => (
    item.fanoutEstimate ||
    fanoutEstimates[item.id] ||
    fanoutEstimates[item.type] ||
    fanoutEstimates[item.label] ||
    null
  ), [fanoutEstimates]);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(600, rect.width - 40),
          height: Math.max(400, Math.min(600, rect.height - 100))
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const findHorizontalPath = useCallback((startX, endX, y, rectangles) => {
    // Create a simple path that goes around rectangles
    const points = [{ x: startX, y }];
    
    // Check if direct path is clear
    const directPathClear = !rectangles.some(rect => 
      y >= rect.y0 - 10 && y <= rect.y1 + 10 && 
      ((startX <= rect.x1 + 10 && startX >= rect.x0 - 10) || 
       (endX <= rect.x1 + 10 && endX >= rect.x0 - 10) ||
       (startX < rect.x0 && endX > rect.x1))
    );
    
    if (directPathClear) {
      points.push({ x: endX, y });
      return points;
    }
    
    // Find path around obstacles
    const sortedRects = rectangles
      .filter(rect => y >= rect.y0 - 10 && y <= rect.y1 + 10)
      .sort((a, b) => a.x0 - b.x0);
    
    let currentX = startX;
    const targetX = endX;
    
    for (const rect of sortedRects) {
      if ((currentX < rect.x0 && targetX > rect.x0) || 
          (currentX > rect.x1 && targetX < rect.x1)) {
        // Need to go around this rectangle
        if (currentX < targetX) {
          // Going right, go around the right side
          points.push({ x: rect.x1 + 15, y });
          currentX = rect.x1 + 15;
        } else {
          // Going left, go around the left side
          points.push({ x: rect.x0 - 15, y });
          currentX = rect.x0 - 15;
        }
      }
    }
    
    points.push({ x: endX, y });
    return points;
  }, []);

  // Smart connector routing function
  const createSmartPath = useCallback((startX, startY, endX, endY, rectangles, corridorY) => {
    // Simple smart routing that avoids rectangles
    const path = d3.path();
    path.moveTo(startX, startY);
    
    // Go down to corridor level
    path.lineTo(startX, corridorY);
    
    // Find the best horizontal path through the corridor
    const horizontalPath = findHorizontalPath(startX, endX, corridorY, rectangles);
    
    // Draw the horizontal segments
    horizontalPath.forEach((point, index) => {
      if (index === 0) return; // Skip first point (already at startX, corridorY)
      path.lineTo(point.x, point.y);
    });
    
    // Go down to target
    path.lineTo(endX, endY);
    
    return path.toString();
  }, [findHorizontalPath]);

  useEffect(() => {
    if (!svgRef.current) return;


    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { width, height } = dimensions;
    const margin = { top: 40, right: 10, bottom: 10, left: 10 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    // Reserve more space for connector corridor
    const connectorCorridor = 32;
    const treemapHeight = innerHeight - connectorCorridor;

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let data = [];
    let colorScale;
    let parentNode = null;

    // Use settings colors if available
    // Defaults: nodes blue, edges green shades
    const defaultNodeColor = settings?.nodeColor || '#000000'; // Black
    const edgeShades = settings?.edgeTypeColors || [
      '#fafafa', '#f7f7f7', '#f5f5f5', '#f2f2f2', '#f0f0f0', '#ededed', '#ebebeb', '#e8e8e8', '#e6e6e6'
    ];
    const rawRectTextScale = Number(settings?.rectTextScale);
    const rectTextScale = Number.isFinite(rawRectTextScale)
      ? Math.min(1.5, Math.max(0.75, rawRectTextScale))
      : 1;
    const scaleRectFont = (fontSize) => Number((fontSize * rectTextScale).toFixed(2));
    const isRuntimeMetric = scentControls?.aggregate === 'runtime' || scentControls?.aggregate === 'runtime_x1000';
    const isFanoutMetric = scentControls?.aggregate === 'fanout';
    const runtimeMultiplier = scentControls?.aggregate === 'runtime_x1000' ? 1000 : 1;
    const useLogNodeSizing = settings?.logScaleNodeSizes === true && currentView !== 'edgeTypes';
    const getLayoutValue = (rawValue) => {
      const numericValue = Number(rawValue);
      const safeValue = Number.isFinite(numericValue) ? Math.max(numericValue, 0) : 0;
      return useLogNodeSizing && safeValue > 0 ? Math.log1p(safeValue) : safeValue;
    };
    const formatRuntimeEstimate = (estimate) => {
      if (!estimate) return 'No estimate';
      const label = `est. ${formatRuntimeDuration(estimate.estimatedMs * runtimeMultiplier)}`;
      return runtimeMultiplier === 1000 ? `${label} (x1000)` : label;
    };
    const withRuntimeSizing = (item) => {
      const runtimeEstimate = getRuntimeEstimate(item);
      const fanoutEstimate = getFanoutEstimate(item);
      const baseValue = item.value ?? item.count;
      const value = isRuntimeMetric && runtimeEstimate
        ? runtimeEstimate.estimatedMs * runtimeMultiplier
        : isFanoutMetric && fanoutEstimate
          ? Math.max(fanoutEstimate.value, 0.001)
          : baseValue;
      return {
        ...item,
        value: getLayoutValue(value),
        count: item.count,
        id: item.type,
        label: item.type,
        canNavigate: item.canNavigate !== false,
        runtimeEstimate,
        fanoutEstimate
      };
    };

    if (currentView === 'nodeTypes') {
      data = nodeTypeSummary.map(withRuntimeSizing);
      // All node type rectangles in blue
      colorScale = () => defaultNodeColor;
    } else if (currentView === 'edgeTypes') {
      data = edgeTypeSummary.map(withRuntimeSizing);
      
      // Edge type rectangles all white
      colorScale = d3.scaleOrdinal(edgeShades);
      
      // Add parent node representation
      parentNode = {
        label: selectedNodeType,
        x: innerWidth / 2 - 80,
        y: -30,
        width: 160,
        height: 25
      };
    } else if (currentView === 'specificNodes') {
      data = buildContinuationSummaries(
        filteredNodes,
        'nodes',
        scentControls || { groupBy: 'Node Type', aggregate: 'count', attribute: null }
      ).map(withRuntimeSizing);
      
      // Specific nodes are still node groups: keep blue
      colorScale = () => defaultNodeColor;
      
      // Add parent edge representation
      parentNode = {
        label: `${selectedNodeType} → ${selectedEdgeType}`,
        x: innerWidth / 2 - 140,
        y: -30,
        width: 280,
        height: 25
      };
    }

    if (data.length === 0) {
      return;
    }

    const unitLabel = currentView === 'edgeTypes' ? 'connections' : 'nodes';
    const isCountMetric = !scentControls || scentControls.aggregate === 'count';
    const getMainLabelFontSize = (d) => {
      const width = d.x1 - d.x0;
      const height = d.y1 - d.y0;
      return scaleRectFont(Math.min(Math.max(11, Math.min(width / 8, height / 4)), 16));
    };
    const getMetricLabelFontSize = (d) => {
      const width = d.x1 - d.x0;
      const height = d.y1 - d.y0;
      return scaleRectFont(Math.min(Math.max(9, Math.min(width / 10, height / 6)), 13));
    };
    const getExpectedLabelFontSize = (d) => {
      const width = d.x1 - d.x0;
      const height = d.y1 - d.y0;
      return scaleRectFont(Math.min(Math.max(9, Math.min(width / 13, height / 9)), 11));
    };
    const getCountText = (item) => `${(item.count ?? item.value ?? 0).toLocaleString()} ${unitLabel}`;
    const getMetricText = (item) => (
      isRuntimeMetric
        ? formatRuntimeEstimate(item.runtimeEstimate)
        : isFanoutMetric
          ? item.fanoutEstimate?.label || 'No fanout'
        : item.metricLabel || getCountText(item)
    );
    const getTooltipContent = (item) => {
      let content = isCountMetric ? getCountText(item) : `${metricLabel}: ${getMetricText(item)}\nCount: ${getCountText(item)}`;
      if (item.canNavigate === false) {
        content += `\nGrouped by: ${item.groupBy || groupByLabel}`;
      } else if (item.runtimeEstimate && (showRuntimeEstimates || isRuntimeMetric)) {
        content += `\nExpected next: ${formatRuntimeEstimate(item.runtimeEstimate)}\n${item.runtimeEstimate.detail}`;
      }
      if (item.fanoutEstimate && isFanoutMetric) {
        content += `\nFanout: ${item.fanoutEstimate.label}\n${item.fanoutEstimate.detail}`;
      }
      return content;
    };


    // Create hierarchy
    const root = d3.hierarchy({ children: data })
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    // Create treemap layout with increased padding for smart routing
    const treemap = d3.treemap()
      .size([innerWidth, treemapHeight])
      .padding(12) // Increased padding for routing space
      .round(true);

    treemap(root);

    // Offset treemap rectangles to leave corridor space
    const yOffset = connectorCorridor;
    root.leaves().forEach(d => {
      d.y0 += yOffset;
      d.y1 += yOffset;
    });

    // Collect rectangle positions for smart routing
    const rectangles = root.leaves().map(d => ({
      x0: d.x0,
      y0: d.y0,
      x1: d.x1,
      y1: d.y1
    }));

    // Draw parent node if it exists
    if (parentNode && (!settings || settings.showConnectors !== false)) {
      g.append('rect')
        .attr('x', parentNode.x)
        .attr('y', parentNode.y)
        .attr('width', parentNode.width)
        .attr('height', parentNode.height)
        // Parent represents a node in edgeTypes view (blue) and an edge context in specificNodes view (green)
        .attr('fill', currentView === 'specificNodes' ? '#f5f5f5' : defaultNodeColor)
        .attr('stroke', '#000000')
        .attr('stroke-width', currentView === 'specificNodes' ? 2 : 2)
        .attr('rx', 4)
        .style('cursor', 'pointer')
        .style('filter', 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))')
        .on('click', () => {
          if (onBackClick) onBackClick();
        });

      g.append('text')
        .attr('x', parentNode.x + parentNode.width / 2)
        .attr('y', parentNode.y + parentNode.height / 2 + 5)
        .attr('text-anchor', 'middle')
        .attr('font-family', 'SF Mono, Monaco, Inconsolata, Courier New, monospace')
        .attr('font-size', scaleRectFont(12))
        .attr('font-weight', '600')
        .attr('fill', currentView === 'specificNodes' ? '#000000' : 'white')
        .text(parentNode.label)
        .style('cursor', 'pointer')
        .on('click', () => {
          if (onBackClick) onBackClick();
        });

      // Add smart connectors from parent to children
      const parentCenterX = parentNode.x + parentNode.width / 2;
      const parentBottomY = parentNode.y + parentNode.height;
      const connectorY = parentBottomY + 30; // Corridor level with more space

      root.leaves().forEach(d => {
        const childCenterX = d.x0 + (d.x1 - d.x0) / 2;
        const childTopY = d.y0;

        // Create smart connector path that avoids rectangles
        const pathData = createSmartPath(
          parentCenterX, 
          parentBottomY, 
          childCenterX, 
          childTopY, 
          rectangles, 
          connectorY
        );

        g.append('path')
          .attr('d', pathData)
          .attr('stroke', currentView === 'edgeTypes' ? '#cccccc' : '#666666')
          .attr('stroke-width', 1.5)
          .attr('fill', 'none')
          .style('opacity', 0.6)
          .attr('stroke-dasharray', 'none');

        // Add connector dots
        // Child connection dot with hover and click
        g.append('circle')
          .attr('cx', childCenterX)
          .attr('cy', childTopY)
          .attr('r', 5)
          .attr('fill', currentView === 'edgeTypes' ? '#999999' : '#666666')
          .attr('stroke', 'none')
          .style('cursor', d.data.canNavigate === false ? 'default' : 'pointer')
          .on('mouseover', function(event) {
            d3.select(this)
              .attr('r', 7)
              .attr('fill', currentView === 'edgeTypes' ? '#666666' : '#000000');
            
            const mouseX = event.clientX;
            const mouseY = event.clientY;
            
            setTooltip({
              show: true,
              x: mouseX,
              y: mouseY - 8,
              title: d.data.label,
              content: getTooltipContent(d.data)
            });
          })
          .on('mouseout', function() {
            d3.select(this)
              .attr('r', 5)
              .attr('fill', currentView === 'edgeTypes' ? '#999999' : '#666666');
            
            setTooltip({ show: false, x: 0, y: 0, title: '', content: '' });
          })
          .on('click', function(event) {
            event.stopPropagation();
            if (d.data.canNavigate === false) return;
            if (currentView === 'nodeTypes') {
              onNodeTypeClick(d.data.id);
            } else if (currentView === 'edgeTypes') {
              onEdgeTypeClick(d.data.id);
            } else if (currentView === 'specificNodes') {
              onNodeTypeClick(d.data.id);
            }
          });
      });

      // Parent connection dot
      g.append('circle')
        .attr('cx', parentCenterX)
        .attr('cy', parentBottomY)
        .attr('r', 5)
        .attr('fill', currentView === 'edgeTypes' ? '#999999' : '#666666')
        .attr('stroke', 'none')
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          d3.select(this)
            .attr('r', 7)
            .attr('fill', currentView === 'edgeTypes' ? '#666666' : '#000000');
          
          const mouseX = event.clientX;
          const mouseY = event.clientY;
          
          setTooltip({
            show: true,
            x: mouseX,
            y: mouseY - 8,
            title: parentNode.label,
            content: 'Parent node'
          });
        })
        .on('mouseout', function() {
          d3.select(this)
            .attr('r', 5)
            .attr('fill', currentView === 'edgeTypes' ? '#999999' : '#666666');
          
          setTooltip({ show: false, x: 0, y: 0, title: '', content: '' });
        });
    }

    // Create treemap rectangles
    const cell = g.selectAll('.cell')
      .data(root.leaves())
      .enter().append('g')
      .attr('class', 'cell')
      .attr('transform', d => `translate(${d.x0},${d.y0})`);

    // Add rectangles
    cell.append('rect')
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0)
      .attr('fill', d => colorScale(d.data.id))
      .attr('stroke', d => {
        // For edge views, use black stroke; for node views, use white stroke
        return currentView === 'edgeTypes' ? '#000000' : '#ffffff';
      })
      .attr('stroke-width', 2)
      .attr('rx', 4)
      .style('cursor', d => d.data.canNavigate === false ? 'default' : 'pointer')
      .style('opacity', 1)
      .style('transition', settings?.animateTransitions ? 'all 0.3s ease' : 'none')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .style('opacity', 0.85);
        
        // Always show tooltip with full information
        const width = d.x1 - d.x0;
        // Position tooltip centered above the rect
        const svgRect = svgRef.current?.getBoundingClientRect();
        const centerX = (svgRect?.left || 0) + margin.left + d.x0 + width / 2;
        const topY = (svgRect?.top || 0) + margin.top + d.y0; // top edge of rect
        
        // Create detailed tooltip content
        let content = getTooltipContent(d.data);
        
        // Add examples if available
        if (d.data.examples && d.data.examples.length > 0) {
          const exampleText = d.data.examples.slice(0, 3).map(ex => 
            typeof ex === 'string' ? ex : (ex.name || `Node ${ex.id}`)
          ).join(', ');
          content += `\nExamples: ${exampleText}${d.data.examples.length > 3 ? '...' : ''}`;
        }
        
        // Add connected types if available
        if (d.data.connectedNodeTypes && d.data.connectedNodeTypes.length > 0) {
          content += `\nConnects to: ${d.data.connectedNodeTypes.slice(0, 3).join(', ')}${d.data.connectedNodeTypes.length > 3 ? '...' : ''}`;
        }
        
        setTooltip({
          show: true,
          x: centerX,
          y: topY - 8, // small gap above rect
          title: d.data.label,
          content: content
        });
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .style('opacity', 1);
        setTooltip({ show: false, x: 0, y: 0, content: '', title: '' });
      })
      .on('click', function(event, d) {
        if (d.data.canNavigate === false) return;
        if (currentView === 'nodeTypes') {
          onNodeTypeClick(d.data.id);
        } else if (currentView === 'edgeTypes') {
          onEdgeTypeClick(d.data.id);
        } else if (currentView === 'specificNodes') {
          // For recursive navigation - clicking a node type shows its edges
          onNodeTypeClick(d.data.id);
        }
      });

    // Add labels with conditional rendering based on size
    cell.append('text')
      .attr('x', 12)
      .attr('y', scaleRectFont(22))
      .attr('font-family', 'SF Mono, Monaco, Inconsolata, Courier New, monospace')
      .attr('font-size', getMainLabelFontSize)
      .attr('font-weight', '600')
      .attr('fill', d => calculateTextColor(colorScale(d.data.id)))
      .each(function(d) {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        
        if (width > 100 * rectTextScale && height > 60 * rectTextScale) {
          // Show full text for larger rectangles
          d3.select(this).text(d.data.label).call(wrapText);
        } else if (width > 60 * rectTextScale && height > 40 * rectTextScale) {
          // Show abbreviated text for medium rectangles
          const maxChars = Math.floor(width / (10 * rectTextScale));
          const text = d.data.label.length > maxChars 
            ? d.data.label.substring(0, maxChars - 2) + '..' 
            : d.data.label;
          d3.select(this).text(text);
        }
        // For very small rectangles, show no text (tooltip will handle it)
      });

    // Add count labels
    cell.append('text')
      .attr('x', 12)
      .attr('y', d => {
        const fontSize = getMainLabelFontSize(d);
        return scaleRectFont(22) + fontSize + scaleRectFont(6);
      })
      .attr('font-family', 'SF Mono, Monaco, Inconsolata, Courier New, monospace')
      .attr('font-size', getMetricLabelFontSize)
      .attr('fill', d => {
        if (isRuntimeMetric && d.data.runtimeEstimate) {
          return RUNTIME_TEXT_COLOR;
        }

        const baseColor = calculateTextColor(colorScale(d.data.id));
        // Make count text slightly more subtle than main text
        return baseColor === '#FFFFFF' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)';
      })
      .each(function(d) {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        
        if (width > 80 * rectTextScale && height > 70 * rectTextScale) {
          const fullText = isCountMetric ? getCountText(d.data) : getMetricText(d.data);
          const maxChars = Math.max(8, Math.floor(width / (7 * rectTextScale)));
          const text = fullText.length > maxChars ? `${fullText.slice(0, maxChars - 2)}..` : fullText;
          d3.select(this).text(text);
        }
      });

    // Add expected next-compute labels where there is room.
    cell.append('text')
      .attr('x', 12)
      .attr('y', d => Math.max(scaleRectFont(18), (d.y1 - d.y0) - scaleRectFont(12)))
      .attr('font-family', 'SF Mono, Monaco, Inconsolata, Courier New, monospace')
      .attr('font-size', getExpectedLabelFontSize)
      .attr('font-weight', '600')
      .attr('fill', RUNTIME_TEXT_COLOR)
      .each(function(d) {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        const estimate = d.data.runtimeEstimate;

        if (!estimate || d.data.canNavigate === false || (!showRuntimeEstimates && !isRuntimeMetric) || width <= 120 * rectTextScale || height <= 95 * rectTextScale) {
          return;
        }

        const fullText = `Expected ${formatRuntimeDuration(estimate.estimatedMs * runtimeMultiplier)}`;
        const maxChars = Math.max(10, Math.floor(width / (7 * rectTextScale)));
        const text = fullText.length > maxChars ? `${fullText.slice(0, maxChars - 2)}..` : fullText;
        d3.select(this).text(text);
      });

    // Add examples or connected types (for larger rectangles only)
    cell.each(function(d) {
      const width = d.x1 - d.x0;
      const height = d.y1 - d.y0;
      
      if (width > 160 * rectTextScale && height > 120 * rectTextScale) {
        const examples = d.data.examples || d.data.connectedNodeTypes || [];
        const maxExamples = Math.max(0, Math.min(3, Math.floor((height - scaleRectFont(112)) / scaleRectFont(16))));
        
        d3.select(this).selectAll('.example')
          .data(examples.slice(0, maxExamples))
          .enter().append('text')
          .attr('class', 'example')
          .attr('x', 12)
          .attr('y', (_, i) => scaleRectFont(75) + i * scaleRectFont(16))
          .attr('font-family', 'Arial, sans-serif')
          .attr('font-size', scaleRectFont(11))
          .attr('fill', () => {
            const baseColor = calculateTextColor(colorScale(d.data.id));
            return baseColor === '#FFFFFF' ? '#D1D5DB' : '#9CA3AF';
          })
          .text(d => {
            const maxLen = Math.floor(width / (8 * rectTextScale));
            if (typeof d === 'string') {
              return d.length > maxLen ? d.substring(0, maxLen) + '...' : d;
            }
            const name = d.name || `Node ${d.id}`;
            return name.length > maxLen ? name.substring(0, maxLen) + '...' : name;
          });
      }
    });

    function wrapText(text) {
      text.each(function(d) {
        const textElement = d3.select(this);
        const words = textElement.text().split(/\s+/).reverse();
        const lineHeight = 1.1;
        const width = d.x1 - d.x0 - 24; // Account for increased padding
        const fontSize = parseFloat(textElement.attr('font-size'));
        
        let word;
        let line = [];
        let lineNumber = 0;
        const y = textElement.attr('y');
        
        textElement.text(null);
        
        let tspan = textElement.append('tspan')
          .attr('x', textElement.attr('x'))
          .attr('y', y);
        
        while ((word = words.pop())) {
          line.push(word);
          tspan.text(line.join(' '));
          
          if (tspan.node().getComputedTextLength() > width && line.length > 1) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            
            tspan = textElement.append('tspan')
              .attr('x', textElement.attr('x'))
              .attr('y', parseFloat(y) + (++lineNumber * lineHeight * fontSize))
              .text(word);
          }
        }
      });
    }


  }, [dimensions, currentView, selectedNodeType, selectedEdgeType, nodeTypeSummary, edgeTypeSummary, filteredNodes, settings, scentControls, metricLabel, groupByLabel, getRuntimeEstimate, getFanoutEstimate, showRuntimeEstimates, createSmartPath, onNodeTypeClick, onEdgeTypeClick, onBackClick]);

  const getTitle = () => {
    if (currentView === 'nodeTypes') return 'Node Types';
    if (currentView === 'edgeTypes') return `Edge Types for ${selectedNodeType}`;
    if (currentView === 'specificNodes') return `Nodes connected via ${selectedEdgeType}`;
    return 'Graph Explorer';
  };

  const getDataCount = () => {
    if (currentView === 'nodeTypes') return nodeTypeSummary.length;
    if (currentView === 'edgeTypes') return edgeTypeSummary.length;
    if (currentView === 'specificNodes') return filteredNodes.length;
    return 0;
  };

  if (isCalculating) {
    return (
      <div className="bg-white rounded shadow-sm border border-vercel-border p-6 h-full relative flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-vercel-black mb-4"></div>
          <div className="text-sm font-mono font-medium text-vercel-black">Calculating Edge Types...</div>
          <div className="text-xs font-mono text-vercel-gray mt-2">
            {calculationProgress || `Analyzing ${selectedNodeType ? `${selectedNodeType} connections` : 'data'}`}
          </div>
          <div className="text-xs font-mono text-vercel-light-gray mt-4">
            This may take a few seconds for large datasets
          </div>
        </div>
      </div>
    );
  }

  const hasAnyNonLeafNodes = (nodes, edgeIdx) => {
    if (!nodes || nodes.length === 0 || !edgeIdx) {
      return false;
    }
    
    const result = nodes.some(node => {
      const outgoingEdges = edgeIdx.bySource?.get(node.id) || [];
      return outgoingEdges.length > 0;
    });
    
    return result;
  };

  const allNodesAreLeaves = currentView === 'specificNodes' && 
    filteredNodes.length > 0 && 
    !hasAnyNonLeafNodes(filteredNodes, edgeIndex);

  if (allNodesAreLeaves) {
    const tableItems = filteredNodes.map(node => ({
      type: 'node',
      data: node
    }));

    return (
      <div ref={containerRef} className="bg-white rounded shadow-sm border border-vercel-border p-3 h-full flex flex-col">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h2 className="text-sm font-mono font-semibold text-vercel-black">
              {getTitle()}
            </h2>
            <div className="text-xs font-mono text-vercel-gray mt-0.5">
              {filteredNodes.length} leaf nodes displayed
            </div>
          </div>
          
          {onBackClick && (
            <button
              onClick={onBackClick}
              className="px-3 py-1.5 bg-vercel-black text-white border border-vercel-black rounded hover:bg-vercel-gray transition-colors text-xs font-mono"
            >
              ← Back
            </button>
          )}
        </div>

        <div className="flex-1 overflow-hidden">
          <DataTableView items={tableItems} />
        </div>
      </div>
    );
  }


  return (
    <div ref={containerRef} className="bg-white rounded shadow-sm border border-vercel-border p-3 h-full relative">
      {/* Header */}
      <div className="flex justify-between items-center gap-3 mb-3">
        <div>
          <h2 className="text-sm font-mono font-semibold text-vercel-black">
            {getTitle()}
          </h2>
          <div className="text-xs font-mono text-vercel-gray mt-0.5">
            {getDataCount()} {currentView === 'specificNodes' ? 'nodes' : 'types'} displayed
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {currentView !== 'nodeTypes' && onBackClick && (
            <button
              onClick={onBackClick}
              className="px-3 py-1.5 bg-vercel-black text-white border border-vercel-black rounded hover:bg-vercel-gray transition-colors text-xs font-mono"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Treemap SVG */}
      <div className="w-full" style={{ height: dimensions.height }}>
        <svg ref={svgRef} className="w-full h-full border border-vercel-border rounded"></svg>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-xs font-mono text-vercel-gray">
        {currentView === 'nodeTypes' && 'Click on a node type to see its edge types'}
        {currentView === 'edgeTypes' && 'Click on an edge type to see connected nodes'}
        {currentView === 'specificNodes' && 'Click on a node type group to explore recursively'}
      </div>

      {/* Enhanced Tooltip */}
      {tooltip.show && (
        <div
          className="fixed bg-vercel-black text-white px-3 py-2 rounded border border-vercel-border text-xs font-mono z-50 pointer-events-none shadow-sm max-w-xs"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
        >
          <div className="font-semibold">{tooltip.title}</div>
          <div className="whitespace-pre-line">
            {tooltip.content.split('\n').map((line, index) => (
              <div
                key={`${line}-${index}`}
                style={{ color: isRuntimeTooltipLine(line) ? RUNTIME_TEXT_COLOR : 'rgba(255, 255, 255, 0.9)' }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TreemapView; 
